import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Edit, Loader2, Navigation, Search, Filter, X, LocateFixed, Upload, Download, FileSpreadsheet, Keyboard, Star, Phone, Globe, Clock, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { GoogleMap, useJsApiLoader, Marker, Circle } from "@react-google-maps/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useTheme } from "@/contexts/ThemeContext";
import { usePermissions, deniedReason } from "@/lib/permissions";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_LIBRARIES = ["places"];

// Sydney — used only as a last-resort fallback when the account has no registered location
const DEFAULT_CENTER = { lat: -33.8688, lng: 151.2093 };

const venueTypes = [
  { value: "cafe", label: "Cafe" },
  { value: "restaurant_cafe", label: "Restaurant / Café" },
  { value: "bar_pub", label: "Bar / Pub" },
  { value: "nightclub", label: "Nightclub" },
  { value: "retail", label: "Retail Store" },
  { value: "supermarket", label: "Supermarket" },
  { value: "gym", label: "Gym / Fitness" },
  { value: "hospital_medical", label: "Hospital / Medical Clinic" },
  { value: "automotive", label: "Automotive" },
  { value: "salon", label: "Salon / Beauty" },
  { value: "tattoo", label: "Tattoo Studio" },
  { value: "other", label: "Other" },
];

const australianStates = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
];

const mapContainerStyle = {
  width: "100%",
  height: "100%"
};

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#373737" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
];

// Light map: keep Google's default light palette but declutter POI/transit labels and
// icons so it matches the clean look of darkMapStyles. Used when the portal is in light mode.
const lightMapStyles = [
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
];

// Clean map options - no Google branding or controls. The `styles` key is overridden per
// render from the portal theme (see mapOptions in the component); darkMapStyles here is
// just the default.
const cleanMapOptions = {
  styles: darkMapStyles,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

// spec 5.6. Mirrors backend/trading_hours.py, which is authoritative - this shapes the
// form, the server normalises what it receives and re-derives the customer-facing text.
const TRADING_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const PLACES_DAY = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** One day's row, seeded from Google's periods when the merchant has not set hours yet. */
function tradingRow(openingHours, day) {
  const stored = openingHours?.days?.[day];
  if (stored) {
    return { closed: !!stored.closed, open: stored.open || "", close: stored.close || "" };
  }
  const period = (openingHours?.periods || []).find(
    (p) => PLACES_DAY[p?.open?.day] === day
  );
  if (!period) return { closed: true, open: "", close: "" };
  const pad = (h, m) => `${String(h ?? 0).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`;
  return {
    closed: false,
    open: pad(period.open?.hour, period.open?.minute),
    close: period.close ? pad(period.close.hour, period.close.minute) : "",
  };
}

/** Write one day back, materialising the other six so a partial edit is not ambiguous. */
function setTradingDay(setFormData, day, row) {
  setFormData((prev) => {
    const days = {};
    TRADING_DAYS.forEach((d) => {
      days[d] = d === day ? row : tradingRow(prev.opening_hours, d);
    });
    return { ...prev, opening_hours: { ...(prev.opening_hours || {}), days } };
  });
}

export default function Locations() {
  const { token } = useAuth();
  const { can: canDo, role } = usePermissions();
  const canWriteLocations = canDo("locations:write");
  const { theme } = useTheme();
  // Map colour scheme follows the portal light/dark theme instead of being hardcoded dark.
  const mapOptions = useMemo(
    () => ({ ...cleanMapOptions, styles: theme === "dark" ? darkMapStyles : lightMapStyles }),
    [theme]
  );
  const [locations, setLocations] = useState([]);
  const [operatingSuburbs, setOperatingSuburbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [addressSearch, setAddressSearch] = useState("");
  const [entryMode, setEntryMode] = useState("auto"); // "auto" or "manual"
  const [formData, setFormData] = useState({
    name: "",
    street_address: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    country: "Australia",
    latitude: -33.8688,
    longitude: 151.2093,
    radius_meters: 500,
    venue_type: "retail",
    // Places enrichment fields
    phone: null,
    website: null,
    business_status: null,
    rating: null,
    user_ratings_total: null,
    editorial_summary: null,
    opening_hours: null,
    secondary_opening_hours: null,
    google_place_id: null,
    entry_mode: "auto",
    timezone_offset: null,
  });
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [saving, setSaving] = useState(false);
  const formMapRef = useRef(null);
  const overviewMapRef = useRef(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterState, setFilterState] = useState("all");
  const [filterVenueType, setFilterVenueType] = useState("all");
  const [filterPostcode, setFilterPostcode] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk upload states
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  // Map Google Place types to Booma venue types
  const mapGoogleTypeToVenueType = (types) => {
    const typeMap = {
      cafe: "cafe", coffee_shop: "cafe",
      restaurant: "restaurant_cafe", food: "restaurant_cafe",
      bar: "bar_pub", pub: "bar_pub",
      night_club: "nightclub",
      clothing_store: "retail", shoe_store: "retail", shopping_mall: "retail",
      store: "retail", department_store: "retail", electronics_store: "retail",
      supermarket: "supermarket", grocery_or_supermarket: "supermarket",
      gym: "gym", fitness_center: "gym",
      hospital: "hospital_medical", doctor: "hospital_medical", dentist: "hospital_medical",
      pharmacy: "hospital_medical", physiotherapist: "hospital_medical",
      car_dealer: "automotive", car_repair: "automotive", car_wash: "automotive",
      hair_care: "salon", beauty_salon: "salon", spa: "salon",
      tattoo_parlor: "tattoo",
    };
    for (const t of (types || [])) {
      if (typeMap[t]) return typeMap[t];
    }
    return null;
  };

  // Handle address selection from autocomplete
  const handlePlaceSelect = useCallback((addressData) => {
    const mappedVenueType = mapGoogleTypeToVenueType(addressData.types);
    setFormData(prev => ({
      ...prev,
      name: addressData.place_name || prev.name,
      street_address: addressData.street_address,
      suburb: addressData.suburb,
      state: addressData.state || prev.state,
      postcode: addressData.postcode,
      country: addressData.country,
      latitude: addressData.latitude,
      longitude: addressData.longitude,
      // Places enrichment
      phone: addressData.phone,
      website: addressData.website,
      opening_hours: addressData.opening_hours,
      business_status: addressData.business_status,
      rating: addressData.rating,
      user_ratings_total: addressData.user_ratings_total,
      google_place_id: addressData.place_id,
      venue_type: mappedVenueType || prev.venue_type,
      entry_mode: "auto",
      timezone_offset: addressData.utc_offset_minutes ?? null,
    }));
    setEntryMode("auto");
    setMapCenter({ lat: addressData.latitude, lng: addressData.longitude });
    setAddressSearch(addressData.formatted_address);

    // Pan map to new location
    if (formMapRef.current) {
      formMapRef.current.panTo({ lat: addressData.latitude, lng: addressData.longitude });
      formMapRef.current.setZoom(16);
    }

    toast.success("Business details populated from Google Places");
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    fetchLocations();
    fetchOperatingSuburbs();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
      if (response.data.length > 0) {
        const head = response.data.find(l => l.is_head_office) || response.data[0];
        setMapCenter({ lat: head.latitude, lng: head.longitude });
      }
    } catch (error) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  const fetchOperatingSuburbs = async () => {
    try {
      const response = await axios.get(`${API}/accounts/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperatingSuburbs(response.data?.operating_suburbs || []);
    } catch (error) {
      console.error("Failed to fetch operating suburbs:", error);
    }
  };

  // Bulk upload handlers
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setBulkUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/locations/bulk-upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message, {
        description: `Created: ${response.data.created_count}, Geocoded: ${response.data.geocoded_count || 0}, Errors: ${response.data.error_count}`
      });
      
      if (response.data.errors?.length > 0) {
        console.log("Bulk upload errors:", response.data.errors);
      }
      
      fetchLocations();
      setBulkUploadOpen(false);
    } catch (error) {
      console.error("Bulk upload failed:", error);
      toast.error(error.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/locations/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'locations_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Filtered locations based on search and filters
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          location.name?.toLowerCase().includes(query) ||
          location.suburb?.toLowerCase().includes(query) ||
          location.street_address?.toLowerCase().includes(query) ||
          location.postcode?.includes(query);
        if (!matchesSearch) return false;
      }

      // State filter
      if (filterState !== "all" && location.state !== filterState) {
        return false;
      }

      // Venue type filter
      if (filterVenueType !== "all" && location.venue_type !== filterVenueType) {
        return false;
      }

      // Postcode filter
      if (filterPostcode && !location.postcode?.startsWith(filterPostcode)) {
        return false;
      }

      return true;
    });
  }, [locations, searchQuery, filterState, filterVenueType, filterPostcode]);

  // Get unique values for filters
  const uniqueSuburbs = useMemo(() => {
    return [...new Set(locations.map(l => l.suburb))].filter(Boolean).sort();
  }, [locations]);

  const uniquePostcodes = useMemo(() => {
    return [...new Set(locations.map(l => l.postcode))].filter(Boolean).sort();
  }, [locations]);

  // Registered head-office coords (captured at signup) — the sensible map default; Sydney only if none
  const headOfficeCoords = useMemo(() => {
    const head = locations.find(l => l.is_head_office) || locations[0];
    return head ? { lat: head.latitude, lng: head.longitude } : DEFAULT_CENTER;
  }, [locations]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterState("all");
    setFilterVenueType("all");
    setFilterPostcode("");
  };

  const hasActiveFilters = searchQuery || filterState !== "all" || filterVenueType !== "all" || filterPostcode;

  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location updated successfully");
      } else {
        await axios.post(`${API}/locations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this location?")) return;
    try {
      await axios.delete(`${API}/locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      toast.error("Failed to delete location");
    }
  };

  const resetForm = () => {
    setEditingLocation(null);
    setAddressSearch("");
    setEntryMode("auto");
    setFormData({
      name: "",
      street_address: "",
      suburb: "",
      state: "NSW",
      postcode: "",
      country: "Australia",
      latitude: headOfficeCoords.lat,
      longitude: headOfficeCoords.lng,
      radius_meters: 500,
      venue_type: "retail",
      phone: null,
      website: null,
      business_status: null,
      rating: null,
      user_ratings_total: null,
      editorial_summary: null,
      opening_hours: null,
      secondary_opening_hours: null,
      google_place_id: null,
      entry_mode: "auto",
      timezone_offset: null,
    });
  };

  const openNewDialog = () => {
    resetForm();
    setMapCenter(headOfficeCoords);
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setAddressSearch(`${location.street_address}, ${location.suburb}, ${location.state} ${location.postcode}`);
    setEntryMode(location.entry_mode || (location.google_place_id ? "auto" : "manual"));
    setFormData({
      name: location.name,
      street_address: location.street_address,
      suburb: location.suburb,
      state: location.state,
      postcode: location.postcode,
      country: location.country,
      latitude: location.latitude,
      longitude: location.longitude,
      radius_meters: location.radius_meters,
      venue_type: location.venue_type,
      phone: location.phone || null,
      website: location.website || null,
      business_status: location.business_status || null,
      rating: location.rating || null,
      user_ratings_total: location.user_ratings_total || null,
      editorial_summary: location.editorial_summary || null,
      opening_hours: location.opening_hours || null,
      secondary_opening_hours: location.secondary_opening_hours || null,
      google_place_id: location.google_place_id || null,
      entry_mode: location.entry_mode || "auto",
      timezone_offset: location.timezone_offset || null,
    });
    setMapCenter({ lat: location.latitude, lng: location.longitude });
    setDialogOpen(true);
  };

  const onFormMapLoad = useCallback((map) => {
    formMapRef.current = map;
  }, []);

  const onOverviewMapLoad = useCallback((map) => {
    overviewMapRef.current = map;
    if (locations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach(loc => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });
      map.fitBounds(bounds);
      // Prevent zooming in too close when there's only one or few locations
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 12) {
          map.setZoom(12);
        }
      });
    }
  }, [locations]);

  const focusOnLocation = (location) => {
    if (overviewMapRef.current) {
      overviewMapRef.current.panTo({ lat: location.latitude, lng: location.longitude });
      overviewMapRef.current.setZoom(15);
    }
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-destructive">Error loading Google Maps</div>
      </div>
    );
  }

  if (loading || !isLoaded) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading locations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Locations</h1>
          <p className="text-muted-foreground mt-1">
            Manage your business locations for geo-targeting
          </p>
        </div>
        <div className="flex gap-2">
          {/* Bulk Upload Button */}
          <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-upload-btn">
                <Upload className="mr-2 h-4 w-4" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Bulk Upload Locations
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file with multiple locations. Coordinates are automatically determined from the address, so there is no need for latitude/longitude.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                    <Download className="mr-2 h-4 w-4" />
                    Download Template
                  </Button>
                </div>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkUpload}
                    ref={bulkFileInputRef}
                    className="hidden"
                    id="bulk-upload-input"
                  />
                  <label htmlFor="bulk-upload-input" className="cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium">Click to upload CSV</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Required columns: name, address, suburb, state, postcode
                    </p>
                  </label>
                </div>
                {bulkUploading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading and geocoding locations...
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Add Location Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              {/* Round 10: locations:write. */}
              <Button onClick={openNewDialog} data-testid="add-location-btn"
                      disabled={!canWriteLocations}
                      title={canWriteLocations ? undefined : deniedReason(role)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Location
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingLocation ? "Edit Location" : "Add New Location"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Form Fields */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Location Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Main Store"
                      required
                      data-testid="location-name-input"
                    />
                  </div>

                  {/* Auto-fill / Manual Toggle */}
                  {entryMode === "auto" ? (
                    <>
                      {/* Address Autocomplete */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <LocateFixed className="h-4 w-4 text-primary" />
                          Search Business or Address
                        </Label>
                        <AddressAutocomplete
                          defaultValue={addressSearch}
                          onPlaceSelect={handlePlaceSelect}
                          placeholder="Search for a business or address..."
                          searchTypes={["establishment"]}
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            Auto-fills address, opening hours, and business details
                          </p>
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                            onClick={() => {
                              setEntryMode("manual");
                              setFormData(prev => ({
                                ...prev,
                                entry_mode: "manual",
                                google_place_id: null,
                                opening_hours: null,
                                phone: null,
                                website: null,
                                business_status: null,
                                rating: null,
                                user_ratings_total: null,
                                editorial_summary: null,
                              }));
                            }}
                          >
                            <Keyboard className="h-3 w-3" />
                            Enter address manually
                          </button>
                        </div>
                      </div>

                      {/* Address fields - read-only in auto mode after selection */}
                      <div className="space-y-2">
                        <Label htmlFor="street_address">Street Address</Label>
                        <Input
                          id="street_address"
                          value={formData.street_address}
                          readOnly={!!formData.google_place_id}
                          className={formData.google_place_id ? "bg-muted/50" : ""}
                          onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                          placeholder="123 Main Street"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="suburb">Suburb</Label>
                          <Input
                            id="suburb"
                            value={formData.suburb}
                            readOnly={!!formData.google_place_id}
                            className={formData.google_place_id ? "bg-muted/50" : ""}
                            onChange={(e) => setFormData(prev => ({ ...prev, suburb: e.target.value }))}
                            placeholder="Sydney"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="postcode">Postcode</Label>
                          <Input
                            id="postcode"
                            value={formData.postcode}
                            readOnly={!!formData.google_place_id}
                            className={formData.google_place_id ? "bg-muted/50" : ""}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder="2000"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="state">State</Label>
                          <Select value={formData.state} onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))} disabled={!!formData.google_place_id}>
                            <SelectTrigger className={formData.google_place_id ? "bg-muted/50" : ""}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {australianStates.map(state => (
                                <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="venue_type">Venue Type</Label>
                          <Select value={formData.venue_type} onValueChange={(value) => setFormData(prev => ({ ...prev, venue_type: value }))} disabled={!!formData.google_place_id}>
                            <SelectTrigger className={formData.google_place_id ? "bg-muted/50" : ""}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {venueTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Business Details from Google Places - shown after selection */}
                      {formData.google_place_id && (
                        <div className="space-y-3 p-3 rounded-lg bg-secondary/30 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Business Details (from Google)</p>

                          {/* Business Status */}
                          {formData.business_status && (
                            <div className="flex items-center gap-2">
                              {formData.business_status === "OPERATIONAL" ? (
                                <Badge variant="outline" className="text-green-600 border-green-600/30 bg-green-500/10">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Operational
                                </Badge>
                              ) : formData.business_status === "CLOSED_TEMPORARILY" ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-600/30 bg-amber-500/10">
                                  <AlertTriangle className="h-3 w-3 mr-1" /> Temporarily Closed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-red-600 border-red-600/30 bg-red-500/10">
                                  <XCircle className="h-3 w-3 mr-1" /> Permanently Closed
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Rating */}
                          {formData.rating && (
                            <div className="flex items-center gap-2 text-sm">
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                              <span className="font-medium">{formData.rating}</span>
                              {formData.user_ratings_total && (
                                <span className="text-muted-foreground">({formData.user_ratings_total.toLocaleString()} reviews)</span>
                              )}
                            </div>
                          )}

                          {/* Opening Hours */}
                          {formData.opening_hours?.weekday_text?.length > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-sm font-medium">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                Opening Hours
                              </div>
                              <div className="grid gap-0.5 text-xs text-muted-foreground pl-5">
                                {formData.opening_hours.weekday_text.map((line, i) => (
                                  <span key={i}>{line}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Phone */}
                          {formData.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{formData.phone}</span>
                            </div>
                          )}

                          {/* Website */}
                          {formData.website && (
                            <div className="flex items-center gap-2 text-sm">
                              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="truncate text-primary">{formData.website}</span>
                            </div>
                          )}

                          {/* Editorial Summary */}
                          {formData.editorial_summary && (
                            <p className="text-xs text-muted-foreground italic">{formData.editorial_summary}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Manual Entry Mode */}
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Entering address manually</p>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                          onClick={() => setEntryMode("auto")}
                        >
                          <Search className="h-3 w-3" />
                          Search for address
                        </button>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="street_address">Street Address</Label>
                        <Input
                          id="street_address"
                          value={formData.street_address}
                          onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                          placeholder="123 Main Street"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="suburb">Suburb</Label>
                          <Input
                            id="suburb"
                            value={formData.suburb}
                            onChange={(e) => setFormData(prev => ({ ...prev, suburb: e.target.value }))}
                            placeholder="Sydney"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="postcode">Postcode</Label>
                          <Input
                            id="postcode"
                            value={formData.postcode}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder="2000"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="state">State</Label>
                          <Select value={formData.state} onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {australianStates.map(state => (
                                <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="venue_type">Venue Type</Label>
                          <Select value={formData.venue_type} onValueChange={(value) => setFormData(prev => ({ ...prev, venue_type: value }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {venueTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Trading hours (spec 5.6). Editable, whether or not Google knows
                      the venue: opening_hours had only ever been written by the Places
                      enrichment, so a business with no listing had none and no way to
                      add any, and one with wrong Google hours could not fix them.
                      Pre-filled from Places when there is a listing, editable after. */}
                  <div className="space-y-2 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <Label>Trading Hours</Label>
                      <span className="text-xs text-muted-foreground">
                        Shown to customers in Discovery
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {TRADING_DAYS.map((day) => {
                        const row = tradingRow(formData.opening_hours, day);
                        return (
                          <div key={day} className="flex items-center gap-2">
                            <span className="w-24 text-sm capitalize">{day}</span>
                            <label className="flex items-center gap-1.5 text-xs text-muted-foreground w-20">
                              <input
                                type="checkbox"
                                checked={!row.closed}
                                onChange={(e) => setTradingDay(setFormData, day, {
                                  ...row, closed: !e.target.checked,
                                })}
                                data-testid={`hours-open-${day}`}
                              />
                              Open
                            </label>
                            <Input
                              type="time" className="h-8 w-28"
                              value={row.open || ""}
                              disabled={row.closed}
                              onChange={(e) => setTradingDay(setFormData, day, { ...row, open: e.target.value })}
                              data-testid={`hours-from-${day}`}
                            />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input
                              type="time" className="h-8 w-28"
                              value={row.close || ""}
                              disabled={row.closed}
                              onChange={(e) => setTradingDay(setFormData, day, { ...row, close: e.target.value })}
                              data-testid={`hours-to-${day}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Leave a day unticked if you are closed. These are your hours, and they
                      override anything we found on Google.
                    </p>
                  </div>

                  {/* Hidden fields */}
                  <input type="hidden" value={formData.radius_meters} />
                  <input type="hidden" value={formData.latitude} />
                  <input type="hidden" value={formData.longitude} />
                </div>

                {/* Google Map */}
                <div className="space-y-2">
                  <Label>Pin Location on Map</Label>
                  <p className="text-xs text-muted-foreground">Click on map to adjust pin position, or use address search above</p>
                  <div className="h-[400px] rounded-lg overflow-hidden border border-border">
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={13}
                      onClick={handleMapClick}
                      onLoad={onFormMapLoad}
                      options={mapOptions}
                    >
                      <Marker 
                        position={{ lat: formData.latitude, lng: formData.longitude }}
                      />
                      <Circle
                        center={{ lat: formData.latitude, lng: formData.longitude }}
                        radius={formData.radius_meters}
                        options={{
                          strokeColor: "#3B82F6",
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          fillColor: "#3B82F6",
                          fillOpacity: 0.2,
                        }}
                      />
                    </GoogleMap>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-location-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editingLocation ? "Update Location" : "Create Location"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Map Overview - illustrates the account's locations and their geographic spread.
          With no locations there's nothing to plot, so show an empty state rather than an
          arbitrary fallback view/pin. */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {locations.length === 0 ? (
            <div className="h-[400px] rounded-lg overflow-hidden flex flex-col items-center justify-center gap-2 text-center bg-muted/30">
              <MapPin className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">No locations set</p>
              <p className="text-xs text-muted-foreground/70">Add a location to see it mapped here.</p>
            </div>
          ) : (
            <div className="h-[400px] rounded-lg overflow-hidden">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={headOfficeCoords}
                zoom={8}
                onLoad={onOverviewMapLoad}
                options={mapOptions}
              >
                {filteredLocations.map(location => (
                  <div key={location.id}>
                    <Marker
                      position={{ lat: location.latitude, lng: location.longitude }}
                      title={location.name}
                    />
                    <Circle
                      center={{ lat: location.latitude, lng: location.longitude }}
                      radius={location.radius_meters}
                      options={{
                        strokeColor: "#3B82F6",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#3B82F6",
                        fillOpacity: 0.15,
                      }}
                    />
                  </div>
                ))}
              </GoogleMap>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, suburb, address, or postcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-locations"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-primary/10" : ""}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {[filterState !== "all", filterVenueType !== "all", filterPostcode].filter(Boolean).length}
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} size="icon">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-border">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Select value={filterState} onValueChange={setFilterState}>
                    <SelectTrigger>
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {australianStates.map(state => (
                        <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Venue Type</Label>
                  <Select value={filterVenueType} onValueChange={setFilterVenueType}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {venueTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Postcode</Label>
                  <Input
                    placeholder="e.g. 2000"
                    value={filterPostcode}
                    onChange={(e) => setFilterPostcode(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {filteredLocations.length} of {locations.length} locations
        </span>
      </div>

      {/* Operating Suburbs Section */}
      {operatingSuburbs.length > 0 && (
        <Card className="bg-card border-border border-dashed border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                <Navigation className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold">Target Market Areas</h3>
                  <Badge variant="outline" className="text-xs">Ad Targeting</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  These suburbs are included in your ad targeting zone. Your ads will be shown to passengers traveling to or through these areas.
                </p>
                <div className="flex flex-wrap gap-2">
                  {operatingSuburbs.map((sub, i) => (
                    <Badge key={i} variant="secondary" className="px-3 py-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {sub.suburb}, {sub.state} {sub.postcode}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  To update target areas, contact support or edit in your Profile settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Locations List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredLocations.map(location => (
              <div 
                key={location.id} 
                className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors"
                data-testid={`location-row-${location.id}`}
              >
                {/* Left: Icon + Location Info - Single Line */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div 
                    className="p-2 rounded-lg bg-primary/10 cursor-pointer hover:bg-primary/20 transition-colors shrink-0"
                    onClick={() => focusOnLocation(location)}
                    title="Show on map"
                  >
                    <MapPin className="h-4 w-4 text-primary" />
                  </div>
                  
                  {/* Name + Venue Type */}
                  <div className="flex items-center gap-2 min-w-0 shrink-0">
                    <h3 className="font-semibold truncate max-w-[160px]">{location.name}</h3>
                    <Badge variant="outline" className="text-[10px] capitalize shrink-0 py-0">
                      {location.venue_type}
                    </Badge>
                  </div>

                  {/* Address - Single Line */}
                  <div className="hidden md:flex items-center gap-1 text-sm text-muted-foreground truncate flex-1 min-w-0">
                    <span className="truncate">{location.street_address}, {location.suburb}, {location.state} {location.postcode}</span>
                  </div>
                </div>

                {/* GPS + Radius - Compact */}
                <div className="hidden lg:flex items-center gap-4 text-xs text-muted-foreground px-4 shrink-0">
                  <span className="font-mono">{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</span>
                  <span className="text-primary font-medium">{location.radius_meters}m</span>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => openEditDialog(location)}
                    className="h-7 w-7 p-0"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" 
                    onClick={() => handleDelete(location.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {filteredLocations.length === 0 && locations.length > 0 && (
            <div className="p-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No matching locations</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your search or filters</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {locations.length === 0 && (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-12 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No locations yet</h3>
            <p className="text-muted-foreground mb-4">Add your first business location to start geo-targeting</p>
            <Button onClick={openNewDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Location
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
